import { useState } from "react";
import { toast } from "sonner";
import { HeartIcon, XIcon, CopyIcon, WalletIcon, CheckIcon } from "@phosphor-icons/react";
import { ApiHealthMeter } from "@/components/ApiHealthMeter";

const TIP_ADDRESS = "EqrsTMVivvWWvzYEVFtee8NQMF1j2AruMN8iUqv79FeR";
const KEY = "alphaapeing_tip_dismissed";
const short = `${TIP_ADDRESS.slice(0, 6)}…${TIP_ADDRESS.slice(-6)}`;
const solanaPay = `solana:${TIP_ADDRESS}`;
const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(solanaPay)}`;

const copy = async () => {
  try {
    await navigator.clipboard.writeText(TIP_ADDRESS);
    toast.success("SOL address copied — thank you for keeping us free! 🦍");
  } catch {
    toast.error("Couldn't copy — select the address manually");
  }
};

const TipModal = ({ onClose }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div data-testid="tip-modal" className="fixed inset-0 z-[130] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-neon/25 max-w-sm w-full p-6 space-y-4 text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-bold text-lg"><HeartIcon size={20} weight="fill" className="text-neon" /> Keep AlphaApeing free</div>
          <button data-testid="tip-close-btn" onClick={onClose} className="p-1 text-muted-foreground hover:text-white"><XIcon size={20} /></button>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          No ads, no paywall — ever. If AlphaApeing helped you catch a runner, a little SOL keeps the lights (and the data feeds) on. 🦍
        </p>
        <div className="bg-white p-3 inline-block mx-auto">
          <img src={qrSrc} alt="Solana tip QR" width={200} height={200} className="block" onError={(e) => { e.target.style.display = "none"; }} />
        </div>
        <div className="space-y-1">
          <p className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground">Send SOL to</p>
          <p data-testid="tip-address" className="font-mono text-xs text-white break-all bg-black/40 border border-white/10 px-3 py-2">{TIP_ADDRESS}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="tip-copy-btn" onClick={async () => { await copy(); setCopied(true); setTimeout(() => setCopied(false), 1500); }} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors">
            {copied ? <CheckIcon size={16} className="text-neon" /> : <CopyIcon size={16} />} {copied ? "Copied" : "Copy address"}
          </button>
          <a data-testid="tip-wallet-btn" href={solanaPay} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-neon text-black text-sm font-semibold hover:bg-neon/90 transition-colors">
            <WalletIcon size={16} weight="fill" /> Open in wallet
          </a>
        </div>
        <p className="text-[10px] text-muted-foreground">Solana only. Double-check the address before sending.</p>
      </div>
    </div>
  );
};

export const TipBanner = () => {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(KEY) === "1");
  const [open, setOpen] = useState(false);

  const close = () => { localStorage.setItem(KEY, "1"); setDismissed(true); };

  return (
    <>
      <div data-testid="bottom-stack" className="fixed bottom-0 left-0 right-0 z-40">
        <ApiHealthMeter />
        {!dismissed && (
          <div data-testid="tip-banner" className="bg-black/85 backdrop-blur-xl border-t border-neon/25">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
              <HeartIcon size={18} weight="fill" className="text-neon shrink-0" />
              <p className="text-xs sm:text-sm text-white flex-1 min-w-0">
                <span className="font-semibold">AlphaApeing is free — no ads, no paywall.</span>
                <span className="hidden sm:inline text-muted-foreground"> Chip in a little SOL to keep it running, Wikipedia-style.</span>
              </p>
              <button data-testid="tip-open-btn" onClick={() => setOpen(true)} className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 bg-neon text-black font-semibold text-xs sm:text-sm hover:bg-neon/90 transition-colors shrink-0">
                <HeartIcon size={14} weight="fill" /> Tip SOL
              </button>
              <button data-testid="tip-copy-inline" onClick={copy} title="Copy SOL address" className="hidden md:flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-muted-foreground text-xs font-mono hover:text-neon hover:border-neon/40 transition-colors shrink-0">
                <CopyIcon size={14} /> {short}
              </button>
              <button data-testid="tip-dismiss-btn" onClick={close} title="Dismiss" className="p-1 text-muted-foreground hover:text-white shrink-0"><XIcon size={16} /></button>
            </div>
          </div>
        )}
      </div>
      {open && <TipModal onClose={() => setOpen(false)} />}
    </>
  );
};
